import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Crown, Check, Lock, Sparkles, X, CreditCard } from 'lucide-react';
import { api } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import type { PlanTier } from '@blogforge/shared';
import { formatDate, formatMoney } from '../lib/billing-format.js';

// ─────────────────────────────────────────────────────────────────
// Plan — subscription tier for the active workspace. Distinct from
// Credits (the metered AI wallet). Individual is purchasable today
// with no auto-renew: each payment buys one term (termDays, normally
// 30). Only the workspace OWNER manages the plan. Trials (14 days)
// are one per workspace AND one per owner user.
// ─────────────────────────────────────────────────────────────────

const PURCHASABLE_PLAN = 'individual';

export function PlansPage() {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['billing', 'plans', activeWorkspaceId],
    queryFn: () => api.billing.plans(),
    enabled: Boolean(activeWorkspaceId),
  });

  const current = data?.current ?? null;
  const canManage = data?.canManage ?? false;
  const trial = data?.trial ?? null;
  const termDays = data?.termDays ?? 30;
  const plans = data?.plans ?? [];

  const [actionOpen, setActionOpen] = useState(false);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['billing', 'plans'] });
    void qc.invalidateQueries({ queryKey: ['billing', 'overview'] });
  };

  const currentTierId = current?.plan ?? 'free';
  const priceFor = (plan: PlanTier) => `${formatMoney(plan.monthlyPriceMinor, plan.currency)}/mo`;
  const payLabel = `Pay ${formatMoney(
    plans.find((p) => p.id === PURCHASABLE_PLAN)?.monthlyPriceMinor ?? 0,
    'NGN',
  )} for ${termDays} days`;

  return (
    <div>
      {/* ── Current plan banner ─────────────────────────────── */}
      <div className="surface-row flex flex-wrap items-center gap-5 px-5 py-5">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]">
          <Crown size={22} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold tracking-wider text-muted uppercase">
            Current plan
          </p>
          {isLoading ? (
            <p className="text-xl font-bold text-ink">…</p>
          ) : current ? (
            <>
              <p className="text-xl leading-tight font-bold tracking-tight text-ink capitalize">
                {current.plan}
                <span className="ml-2 rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-semibold text-accent normal-case">
                  {current.status}
                </span>
              </p>
              <p className="mt-0.5 text-[11.5px] text-muted">
                {current.status === 'trialing'
                  ? `Free trial runs until ${current.currentPeriodEnd ? formatDate(current.currentPeriodEnd) : 'soon'}`
                  : current.currentPeriodEnd
                    ? `Paid through ${formatDate(current.currentPeriodEnd)} — no auto-renew`
                    : 'Active'}
                {current.provider ? ` · billed via ${current.provider}` : ''}
              </p>
            </>
          ) : (
            <>
              <p className="text-xl leading-tight font-bold tracking-tight text-ink">Free</p>
              <p className="mt-0.5 text-[11.5px] text-muted">
                No subscription yet — Individual is pay-per-term (no auto-renew), or try a free
                trial.
              </p>
            </>
          )}
        </div>

        {canManage && current && current.plan === PURCHASABLE_PLAN && (
          <button
            type="button"
            onClick={() => setActionOpen(true)}
            className="btn-primary px-4 py-2 text-[13px]"
          >
            <CreditCard size={14} />
            {current.status === 'trialing' ? `Upgrade now — ${payLabel}` : `Extend — ${payLabel}`}
          </button>
        )}
      </div>

      {!canManage && (
        <p className="mt-3 flex items-center gap-1.5 text-[12px] text-muted">
          <Lock size={12} />
          Only the workspace owner can start trials or buy periods. Ask the owner to manage the
          plan.
        </p>
      )}

      {/* ── Tier catalog ────────────────────────────────────── */}
      <div className="mt-8">
        <h2 className="mb-1 text-[15px] font-bold tracking-tight text-ink">Choose a plan</h2>
        <p className="mb-4 text-[12.5px] text-muted">
          Monthly pricing in NGN, billed per term with no auto-renew. Credits (the AI wallet) are
          bought separately on the Credits tab.
        </p>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => {
            const isCurrent = plan.id === currentTierId;
            const isFree = plan.id === 'free';
            const purchasable = plan.id === PURCHASABLE_PLAN;
            const enabled = canManage && purchasable && !isCurrent;

            return (
              <div
                key={plan.id}
                className={`surface-row flex flex-col p-4 ${
                  plan.highlighted ? 'shadow-[inset_0_0_0_1px_var(--color-accent-border)]' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[13px] font-bold tracking-tight text-ink">{plan.name}</p>
                  {isCurrent && (
                    <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-semibold text-accent">
                      Current
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[17px] leading-none font-bold text-ink">
                  {isFree ? 'Free' : priceFor(plan)}
                </p>
                <p className="mt-1.5 min-h-8 text-[11.5px] leading-snug text-muted">
                  {plan.description}
                </p>

                <ul className="mt-3 flex-1 space-y-1.5">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-1.5 text-[11.5px] text-ink-soft">
                      <Check size={12} strokeWidth={2.4} className="mt-0.5 shrink-0 text-accent" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => enabled && setActionOpen(true)}
                  disabled={!enabled}
                  title={
                    isCurrent
                      ? 'This is your current plan'
                      : !canManage
                        ? 'Only the workspace owner can manage the plan'
                        : purchasable
                          ? 'Subscribe to Individual'
                          : isFree
                            ? 'Free tier'
                            : 'Not available yet'
                  }
                  className={`mt-4 px-3 py-2 text-[12.5px] ${
                    isCurrent ? 'btn-ghost cursor-default' : enabled ? 'btn-primary' : 'btn-ghost opacity-60'
                  }`}
                >
                  {isCurrent ? (
                    'Current plan'
                  ) : !enabled ? (
                    <>
                      <Lock size={12} className="mr-1.5 inline" />
                      {isFree ? 'Free tier' : purchasable ? 'Owner only' : 'Coming soon'}
                    </>
                  ) : (
                    `Choose ${plan.name}`
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {trial && !trial.available && !current && (
          <p className="mt-4 flex items-center gap-1.5 text-[12px] text-muted">
            <Lock size={12} />
            The free trial has been used for this workspace/account — you can still pay for a term.
          </p>
        )}
      </div>

      {/* ── Action modal (owner) ───────────────────────────── */}
      {actionOpen && (
        <ActionModal
          trialAvailable={trial?.available ?? false}
          trialDays={trial?.days ?? 14}
          payLabel={payLabel}
          onStartTrial={async () => {
            await api.billing.subscriptions.startTrial();
            refresh();
            setActionOpen(false);
          }}
          onPay={async () => {
            const checkout = await api.billing.subscriptions.checkout({
              planId: PURCHASABLE_PLAN,
            });
            window.location.assign(checkout.authorizationUrl);
          }}
          onClose={() => setActionOpen(false)}
        />
      )}
    </div>
  );
}

function ActionModal({
  trialAvailable,
  trialDays,
  payLabel,
  onStartTrial,
  onPay,
  onClose,
}: {
  trialAvailable: boolean;
  trialDays: number;
  payLabel: string;
  onStartTrial: () => Promise<void>;
  onPay: () => void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState<'trial' | 'pay' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (kind: 'trial' | 'pay', fn: () => Promise<void> | void) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm">
      <div className="surface-row w-full max-w-sm p-5">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-[15px] font-bold tracking-tight text-ink">Individual plan</h3>
            <p className="mt-0.5 text-[12px] text-muted">No auto-renew — each payment buys a term.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="btn-ghost h-8 w-8 rounded-lg px-0"
          >
            <X size={15} />
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {trialAvailable && (
            <button
              type="button"
              onClick={() => run('trial', onStartTrial)}
              disabled={busy !== null}
              className="btn-primary w-full justify-center px-3 py-2.5 text-[13px]"
            >
              <Sparkles size={14} />
              {busy === 'trial' ? 'Starting…' : `Start ${trialDays}-day free trial`}
            </button>
          )}
          <button
            type="button"
            onClick={() => run('pay', onPay)}
            disabled={busy !== null}
            className="btn-primary w-full justify-center px-3 py-2.5 text-[13px] opacity-90"
          >
            <CreditCard size={14} />
            {busy === 'pay' ? 'Redirecting…' : payLabel}
          </button>
          {error && (
            <p className="rounded-lg border border-danger/20 bg-danger/8 px-3 py-2 text-[12px] text-danger">
              {error}
            </p>
          )}
          {!trialAvailable && (
            <p className="flex items-center gap-1.5 pt-1 text-[11.5px] text-muted">
              <Lock size={11} />
              Trial already used on this workspace/account.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
